import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/apiClient';
import './Jin10NewsPage.css';
import '../styles/pro-page.css';

const topics = ['黄金', '原油', '美联储', '非农', '通胀'];

function formatTime(value) {
  if (!value) return '';
  return String(value).replace(/^\d{4}-/, '').slice(0, 16);
}

function Jin10NewsPage() {
  const [keyword, setKeyword] = useState('');
  const [activeTopic, setActiveTopic] = useState('');
  const [flash, setFlash] = useState([]);
  const [news, setNews] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const searchKeyword = useMemo(() => keyword.trim() || activeTopic, [keyword, activeTopic]);

  useEffect(() => {
    let cancelled = false;

    async function loadJin10() {
      setLoading(true);
      setError('');
      try {
        const [flashResult, newsResult, calendarResult] = await Promise.all([
          api.getJin10({ type: 'flash', keyword: searchKeyword }),
          api.getJin10({ type: 'news', keyword: searchKeyword }),
          api.getJin10({ type: 'calendar' }),
        ]);
        if (cancelled) return;
        setFlash(flashResult.items || []);
        setNews(newsResult.items || []);
        setCalendar(calendarResult.calendar || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '金十数据暂时不可用');
          setFlash([]);
          setNews([]);
          setCalendar([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadJin10();
    const timer = setInterval(loadJin10, 90000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [searchKeyword]);

  return (
    <main className="pro-page jin10-page">
      <div className="pro-page-inner">
        <header className="pro-hero jin10-hero">
          <p className="pro-hero-label">Jin10 MCP</p>
          <h1>金十市场雷达</h1>
          <span className="pro-hero-desc">把快讯、深度资讯和财经日历放在同一屏，给社区用户做每日扫盘入口。</span>
        </header>

        <section className="jin10-toolbar" aria-label="金十资讯筛选">
          <div className="jin10-search">
            <span>主题</span>
            <input
              value={keyword}
              onChange={(event) => {
                setKeyword(event.target.value);
                setActiveTopic('');
              }}
              placeholder="搜索黄金、美联储、非农..."
            />
          </div>
          <div className="jin10-topic-row">
            {topics.map((topic) => (
              <button
                key={topic}
                type="button"
                className={activeTopic === topic ? 'active' : ''}
                onClick={() => {
                  setKeyword('');
                  setActiveTopic(activeTopic === topic ? '' : topic);
                }}
              >
                {topic}
              </button>
            ))}
          </div>
        </section>

        {error && <div className="jin10-error">{error}</div>}
        {loading && <div className="jin10-loading">正在同步金十数据...</div>}

        <div className="jin10-layout">
          <section className="jin10-feed" aria-label="金十快讯">
            <div className="pro-section-head">
              <h2>7×24 快讯</h2>
              <span>{searchKeyword ? `主题：${searchKeyword}` : '实时市场异动与政策消息'}</span>
            </div>
            <div className="jin10-timeline">
              {flash.slice(0, 12).map((item) => (
                <article className={item.important ? 'jin10-flash important' : 'jin10-flash'} key={item.id}>
                  <time>{formatTime(item.time)}</time>
                  <div>
                    {item.important && <strong>重要</strong>}
                    <p>{item.title || item.content}</p>
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer">查看原文</a>}
                  </div>
                </article>
              ))}
              {!loading && !flash.length && <div className="jin10-empty">暂无匹配快讯</div>}
            </div>
          </section>

          <aside className="jin10-side">
            <section className="jin10-panel">
              <div className="jin10-panel-head">
                <h2>深度资讯</h2>
                <span>News</span>
              </div>
              <div className="jin10-news-list">
                {news.slice(0, 5).map((item) => (
                  <a className="jin10-news-item" href={item.url || '#'} target="_blank" rel="noreferrer" key={item.id}>
                    <strong>{item.title}</strong>
                    <span>{item.introduction}</span>
                    <time>{formatTime(item.time)}</time>
                  </a>
                ))}
                {!loading && !news.length && <div className="jin10-empty">暂无资讯</div>}
              </div>
            </section>

            <section className="jin10-panel">
              <div className="jin10-panel-head">
                <h2>财经日历</h2>
                <span>Calendar</span>
              </div>
              <div className="jin10-calendar-list">
                {calendar.slice(0, 6).map((item) => (
                  <article className="jin10-calendar-item" key={`${item.time}-${item.title}`}>
                    <div>
                      <time>{formatTime(item.time)}</time>
                      <strong>{item.title}</strong>
                    </div>
                    <dl>
                      <span><dt>前值</dt><dd>{item.previous || '-'}</dd></span>
                      <span><dt>预期</dt><dd>{item.consensus || '-'}</dd></span>
                      <span><dt>公布</dt><dd>{item.actual || '-'}</dd></span>
                    </dl>
                    {item.affect && <em>{item.affect}</em>}
                  </article>
                ))}
                {!loading && !calendar.length && <div className="jin10-empty">暂无日历</div>}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default Jin10NewsPage;
